import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';

import { ApiError } from '../api/client';
import {
  MigrationDevice,
  ReadinessOverrideResult,
  setReadinessOverride,
} from '../api/migrations';

import EnvironmentEcho from './EnvironmentEcho';
// FD-206 — grant or clear a preflight-readiness override (FD-ADR-027 / FD-205).
//
// The override is not a convenience: with no device-side remediate-and-resubmit
// loop (FD-ADR-027 amendment A2), it is the ONLY way to release a held device. So
// this dialog is bulk-capable by default and the reason is mandatory — the
// override's entire justification is that it leaves a decision trail, and the
// server rejects an unattributable or unexplained grant outright.
//
// The operator identity is NOT collected here. It is taken server-side from the
// EasyAuth principal, because an identity the client could supply would not be
// an audit trail.

export interface ReadinessOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  // Devices selected on the parent grid. Read-only receipt — selection is not
  // editable inside the dialog (the ConfirmRemoveDevicesModal precedent).
  selectedDevices: MigrationDevice[];
  // `false` clears an existing override instead of granting one.
  grant?: boolean;
  onApplied?: () => void;
}

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', rowGap: '12px' },
  serials: {
    maxHeight: '160px',
    overflowY: 'auto',
    fontFamily: 'var(--fontFamilyMonospace)',
    fontSize: '12px',
  },
  destructive: {
    backgroundColor: '#b91c1c',
    color: '#ffffff',
    border: 'none',
    ':hover': { backgroundColor: '#991b1b', color: '#ffffff' },
    ':active': { backgroundColor: '#7f1d1d', color: '#ffffff' },
    ':disabled': { backgroundColor: '#fca5a5', color: '#ffffff' },
  },
});

const MIN_REASON = 4;

export default function ReadinessOverrideDialog({
  open,
  onOpenChange,
  migrationId,
  selectedDevices,
  grant = true,
  onApplied,
}: ReadinessOverrideDialogProps) {
  const styles = useStyles();
  const [reason, setReason] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReadinessOverrideResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset every piece of local state when the dialog closes, so reopening never
  // shows a previous run's result or a stale reason.
  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const serials = selectedDevices.map((d) => d.serialNumber);
  const reasonInvalid = grant && reason.trim().length < MIN_REASON;

  async function submit() {
    if (isSubmitting || serials.length === 0 || reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await setReadinessOverride(migrationId, serials, grant, reason.trim());
      setResult(res);
    } catch (e) {
      // The server's message is more useful than anything invented here — it names
      // the specific refusal (an unresolvable operator, a missing reason).
      setError(e instanceof ApiError ? e.message : 'The override could not be applied.');
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    const applied = (result?.applied?.length ?? 0) > 0;
    onOpenChange(false);
    if (applied) onApplied?.();
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => (data.open ? onOpenChange(true) : close())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {grant ? 'Override readiness hold' : 'Clear readiness override'}
          </DialogTitle>
          <DialogContent className={styles.body}>
            {/* FD-422 — an override releases a preflight gate on real devices in
                one specific deployment, and is recorded against the operator who
                granted it. */}
            <EnvironmentEcho />
            {result ? (
              <MessageBar intent={result.failed.length || result.notFound.length ? 'warning' : 'success'}>
                <MessageBarBody>
                  <MessageBarTitle>
                    {grant ? 'Override applied' : 'Override cleared'}
                  </MessageBarTitle>
                  {result.applied.length} of {serials.length} device
                  {serials.length === 1 ? '' : 's'} updated
                  {result.notFound.length ? `, ${result.notFound.length} not found` : ''}
                  {result.failed.length ? `, ${result.failed.length} failed` : ''}.
                </MessageBarBody>
              </MessageBar>
            ) : error ? (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Could not apply the override</MessageBarTitle>
                  {error}
                </MessageBarBody>
              </MessageBar>
            ) : (
              <>
                {grant ? (
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>This releases the preflight gate</MessageBarTitle>
                      The device will receive its flight plan even though a required check did not
                      pass. The failing reasons stay recorded, and the override is scoped to the
                      current verdict — if a later manifest fails a different check, the device is
                      held again.
                    </MessageBarBody>
                  </MessageBar>
                ) : null}

                <div>
                  <span className="text-sm text-zinc-600">
                    {serials.length} device{serials.length === 1 ? '' : 's'}:
                  </span>
                  <div className={styles.serials}>{serials.join(', ')}</div>
                </div>

                {grant ? (
                  <Field
                    label="Reason (recorded against every device)"
                    required
                    validationState={reason.length > 0 && reasonInvalid ? 'error' : 'none'}
                    validationMessage={
                      reason.length > 0 && reasonInvalid
                        ? 'Give a reason of at least four characters — this is the audit trail.'
                        : undefined
                    }
                  >
                    <Textarea
                      value={reason}
                      onChange={(_, data) => setReason(data.value)}
                      placeholder="e.g. LTSC fleet, OS build accepted by change 4471"
                      resize="vertical"
                    />
                  </Field>
                ) : null}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {result ? (
              <Button appearance="primary" onClick={close}>
                Close
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={close} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  appearance={grant ? undefined : 'primary'}
                  className={grant ? styles.destructive : undefined}
                  onClick={submit}
                  disabled={isSubmitting || serials.length === 0 || reasonInvalid}
                >
                  {isSubmitting
                    ? 'Applying…'
                    : grant
                      ? `Override ${serials.length} device${serials.length === 1 ? '' : 's'}`
                      : 'Clear override'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
